import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label", "aria-labelledby", "aria-describedby"]
const _hoisted_2 = {
  key: 0,
  class: "product-tour__loading"
}
const _hoisted_3 = {
  key: 1,
  class: "product-tour__shade",
  "aria-hidden": "true"
}
const _hoisted_4 = { class: "product-tour__header" }
const _hoisted_5 = {
  id: "product-tour-body",
  class: "product-tour__body"
}
const _hoisted_6 = { class: "product-tour__copy" }
const _hoisted_7 = { class: "product-tour__footer" }
const _hoisted_8 = {
  key: 0,
  class: "product-tour__progress",
  "aria-live": "polite"
}

import { computed, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue';
import Button from 'primevue/button';
import AuthLoadingState from '@/components/ui/AuthLoadingState.vue';
import { PRODUCT_TOUR_WELCOME, type ProductTourStep } from '@/constants/productTour';
import { useTourPosition } from '@/composables/useTourPosition';

const LOADING_MESSAGE = 'Starting your tour';

export default /*@__PURE__*/_defineComponent({
  __name: 'ProductTourOverlay',
  props: {
    step: {},
    progress: {},
    total: {},
    navigating: { type: Boolean },
    loading: { type: Boolean }
  },
  emits: ["next", "dismiss"],
  setup(__props: any, { emit: __emit }) {

/* eslint-env vue/setup-compiler-macros */
const props = __props;
const emit = __emit;

const dialog = shallowRef<HTMLDialogElement | null>(null);
const card = shallowRef<HTMLElement | null>(null);
const heading = shallowRef<HTMLElement | null>(null);
const currentStep = computed(() => props.step);
const title = computed(() => props.step?.title ?? PRODUCT_TOUR_WELCOME.title);
const actionLabel = computed(() => {
    if (!props.step) return 'Take the tour';
    return props.progress === props.total ? 'Finish' : 'Next';
});
const { spotlightStyle, cardPosition } = useTourPosition(currentStep, card);
let previousFocus: HTMLElement | null = null;
let previousOverflow = '';
let previousPadding: string | null = null;
let previousScroll = { left: 0, top: 0 };

onMounted(() => {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    previousOverflow = document.body.style.overflow;
    previousScroll = { left: window.scrollX, top: window.scrollY };
    document.body.style.overflow = 'hidden';
    // The gutter <html> reserves sits outside the paintable viewport, so no overlay can dim it.
    // Drop it for the tour and give the width straight back as padding: nothing moves.
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    if (gutter > 0) {
        previousPadding = document.body.style.paddingRight;
        document.documentElement.style.setProperty('scrollbar-gutter', 'auto', 'important');
        document.body.style.paddingRight = `${gutter}px`;
    }
    dialog.value?.showModal();
    heading.value?.focus({ preventScroll: true });
});

watch(currentStep, () => heading.value?.focus({ preventScroll: true }), { flush: 'post' });

onBeforeUnmount(() => {
    dialog.value?.close();
    document.body.style.overflow = previousOverflow;
    if (previousPadding !== null) {
        document.documentElement.style.removeProperty('scrollbar-gutter');
        document.body.style.paddingRight = previousPadding;
    }
    window.scrollTo({ ...previousScroll, behavior: 'auto' });
    const fallback = document.querySelector<HTMLElement>('[data-product-tour="help"]');
    const returnTarget = previousFocus?.isConnected && previousFocus !== document.body ? previousFocus : fallback;
    returnTarget?.focus({ preventScroll: true });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createElementVNode("dialog", {
      ref_key: "dialog",
      ref: dialog,
      class: _normalizeClass(["product-tour", { 'product-tour--loading': __props.loading }]),
      "aria-label": __props.loading ? LOADING_MESSAGE : undefined,
      "aria-labelledby": __props.loading ? undefined : 'product-tour-title',
      "aria-describedby": __props.loading ? undefined : 'product-tour-body',
      onCancel: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (emit('dismiss')), ["prevent"]))
    }, [
      (__props.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(AuthLoadingState, { message: LOADING_MESSAGE })
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_unref(spotlightStyle))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "product-tour__spotlight",
                  style: _normalizeStyle(_unref(spotlightStyle)),
                  "aria-hidden": "true"
                }, null, 4))
              : (_openBlock(), _createElementBlock("div", _hoisted_3)),
            _createElementVNode("section", {
              ref_key: "card",
              ref: card,
              class: _normalizeClass(["product-tour__card", {
                        'product-tour__card--wide': !__props.step,
                        'product-tour__card--centered': !_unref(cardPosition),
                    }]),
              style: _normalizeStyle(_unref(cardPosition) ?? undefined)
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("span", {
                  id: "product-tour-title",
                  ref_key: "heading",
                  ref: heading,
                  class: "product-tour__title",
                  tabindex: "-1"
                }, _toDisplayString(title.value), 513),
                _createElementVNode("button", {
                  type: "button",
                  class: "product-tour__close",
                  "aria-label": "Close product tour",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('dismiss')))
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("i", {
                    class: "pi pi-times",
                    "aria-hidden": "true"
                  }, null, -1)
                ]))])
              ]),
              _cache[5] || (_cache[5] = _createElementVNode("span", {
                class: "product-tour__hr",
                "aria-hidden": "true"
              }, null, -1)),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("p", _hoisted_6, _toDisplayString(__props.step ? __props.step.body : _unref(PRODUCT_TOUR_WELCOME).body), 1)
              ]),
              _cache[6] || (_cache[6] = _createElementVNode("span", {
                class: "product-tour__hr",
                "aria-hidden": "true"
              }, null, -1)),
              _createElementVNode("div", _hoisted_7, [
                (__props.step)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(__props.progress) + " of " + _toDisplayString(__props.total), 1))
                  : _createCommentVNode("", true),
                _createVNode(_unref(Button), {
                  type: "button",
                  class: "product-tour__cancel-btn",
                  label: __props.step ? 'Skip' : 'Skip for now',
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('dismiss')))
                }, null, 8, ["label"]),
                _createVNode(_unref(Button), {
                  type: "button",
                  class: "product-tour__action-btn",
                  label: actionLabel.value,
                  disabled: __props.navigating,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('next')))
                }, null, 8, ["label", "disabled"])
              ])
            ], 6)
          ], 64))
    ], 42, _hoisted_1)
  ]))
}
}

})